import { useClerk, useUser } from '@clerk/clerk-react';
import {
  ArrowLeft,
  BookOpen,
  Building2,
  Key,
  LogOut,
  PanelRight,
  Shield,
  Users,
} from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import QalicoLogo from '@/assets/qalico-logo.svg?react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Text } from '@/components/ui/text';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import { useWorkspacesService } from '@/services/workspaces';
import {
  getLastVisitedWorkspaceUname,
  getRestoredAppPath,
} from '@/utils/lastWorkspaceSession';
import { cn } from '@/utils/classUtils';

type NavItem = {
  name: string;
  href: string;
  icon: typeof Shield;
  children?: { name: string; href: string }[];
};

const navigationItems: NavItem[] = [
  {
    name: 'Users',
    href: `${ADMIN_PATH}/${adminPaths.users}`,
    icon: Users,
  },
  {
    name: 'Workspaces',
    href: `${ADMIN_PATH}/${adminPaths.workspaces}`,
    icon: Building2,
  },
  {
    name: 'Shared Knowledge',
    href: `${ADMIN_PATH}/${adminPaths.sharedKnowledge}`,
    icon: BookOpen,
  },
  {
    name: 'Consultants',
    href: `${ADMIN_PATH}/${adminPaths.consultants}`,
    icon: Users,
  },
  {
    name: 'API Clients',
    href: `${ADMIN_PATH}/${adminPaths.apiClients}`,
    icon: Key,
  },
  {
    name: 'Regulatory Watch',
    href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}`,
    icon: Shield,
    children: [
      {
        name: 'Sources',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.sources}`,
      },
      {
        name: 'References',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.references}`,
      },
      {
        name: 'Updates',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.updates}`,
      },
      {
        name: 'Institutions',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.institutions}`,
      },
      {
        name: 'Countries',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.countries}`,
      },
      {
        name: 'Scrape',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.scrape}`,
      },
      {
        name: 'Discarded Items',
        href: `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.discardedItems}`,
      },
    ],
  },
];

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export function AdminSidebar({ isCollapsed, onToggle }: AdminSidebarProps) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const location = useLocation();
  const { workspaces } = useWorkspacesService();
  const restoredPath = getRestoredAppPath(workspaces);
  const appWorkspaceUname =
    getLastVisitedWorkspaceUname(workspaces) ?? workspaces?.[0]?.uname;
  const appHref =
    restoredPath ??
    (appWorkspaceUname ? `/workspace/${appWorkspaceUname}` : '/');

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-card border-r border-border transition-all duration-300 ease-in-out bg-ditto-200',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header with Logo and Toggle */}
      <div className="flex h-fit min-h-16 items-center justify-between p-2">
        {!isCollapsed && (
          <div className="flex items-center py-4 px-3">
            <QalicoLogo />
          </div>
        )}
        <div
          className={cn(
            'flex items-center',
            isCollapsed ? 'flex-col-reverse items-center px-2' : 'space-x-2'
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'h-8 w-8 transition-all duration-300 hover:bg-muted',
              isCollapsed ? 'mx-auto' : '',
              !isCollapsed && 'rotate-180'
            )}
          >
            <PanelRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Back office heading + Go to app link */}
      {!isCollapsed && (
        <div className="flex items-center justify-between px-4 pb-2">
          <Text variant="body-sm" className="font-bold">
            Back office
          </Text>
          <Link
            to={appHref}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Go to app
          </Link>
        </div>
      )}
      {isCollapsed && (
        <div className="flex justify-center pb-2">
          <Link
            to={appHref}
            title="Go to app"
            className="flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
      )}

      <Separator />

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navigationItems.map(item => (
            <div key={item.name}>
              <NavLink
                to={item.href}
                end={!item.children}
                className={({ isActive }) =>
                  cn(
                    'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isCollapsed ? 'justify-center' : 'space-x-3',
                    isActive
                      ? 'bg-yellow-400 font-medium'
                      : 'hover:bg-ditto-500 hover:font-medium font-light'
                  )
                }
                title={isCollapsed ? item.name : undefined}
              >
                <item.icon className="h-4 w-4" />
                {!isCollapsed && <span>{item.name}</span>}
              </NavLink>
              {!isCollapsed &&
                item.children &&
                location.pathname.includes(item.href) && (
                  <div className="relative ml-[1.625rem] mt-1 space-y-0.5 pl-3 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-border">
                    {item.children.map(child => (
                      <NavLink
                        key={child.name}
                        to={child.href}
                        end
                        className={({ isActive }) =>
                          cn(
                            'flex items-center rounded-md px-2.5 py-1.5 text-sm transition-colors',
                            isActive
                              ? 'font-medium text-foreground bg-ditto-500'
                              : 'font-light text-muted-foreground hover:text-foreground hover:bg-ditto-500'
                          )
                        }
                      >
                        <span>{child.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Bottom Section - User Info & Sign Out */}
      <div className="px-3 py-4">
        <Separator className="mb-4" />
        {user && (
          <div className="space-y-2">
            <div
              className={cn(
                'flex items-center space-x-3 p-2 rounded-lg',
                isCollapsed ? 'justify-center' : ''
              )}
            >
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user.fullName || user.primaryEmailAddress?.emailAddress}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.primaryEmailAddress?.emailAddress}
                  </p>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => signOut()}
              className={cn(
                'w-full justify-start text-muted-foreground hover:text-foreground',
                isCollapsed ? 'justify-center px-2' : 'px-3'
              )}
              title={isCollapsed ? 'Sign out' : undefined}
            >
              <LogOut className="h-4 w-4" />
              {!isCollapsed && <span className="ml-2">Sign out</span>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
